// src/routes/reports/index.tsx
import { Icon, IconName } from '../../components/ui/Icon';
import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { motion } from 'framer-motion';
import { format, subDays, isAfter, parseISO } from 'date-fns';
import { assetStore } from '../../stores/assetStore';
import { riskStore } from '../../stores/riskStore';
import { nonInfoSecRiskStore } from '../../stores/nonInfoSecRiskStore';
import { riskMatrixStore, riskMatrixActions } from '../../stores/riskMatrixStore';
import { NavigationSidebar } from '../../components/layout/NavigationSidebar';

import { ModuleHeader } from '../../components/layout/ModuleHeader';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { iso27001Controls, controlThemes } from '../../data/iso27001Controls';
import { formatDate, calculateExpiry, getRAGStatus } from '../../utils/dateUtils';
import { exportToCSV } from '../../utils/exportUtils';
import type { Asset } from '../../types/asset';
import type { Risk } from '../../types/risk';
import type { NonInfoSecRisk } from '../../types/nonInfoSecRisk';

export const Route = createFileRoute('/reports/')({
  component: ReportsModule,
});

// Field definitions for report configuration
const allFields = [
  { id: 'referenceNumber', label: 'Reference Number', group: 'Basic' },
  { id: 'make', label: 'Make', group: 'Basic' },
  { id: 'model', label: 'Model', group: 'Basic' },
  { id: 'assetGroup', label: 'Asset Group', group: 'Basic' },
  { id: 'sku', label: 'SKU', group: 'Basic' },
  { id: 'quantity', label: 'Quantity', group: 'Basic' },
  { id: 'location', label: 'Location', group: 'Basic' },
  { id: 'owner', label: 'Asset Owner', group: 'Basic' },
  { id: 'isISO27001', label: 'Info Sec', group: 'Compliance' },
  { id: 'iso27001Category', label: 'Info Sec Category', group: 'Compliance' },
  { id: 'auditDate', label: 'Last Audit', group: 'Dates' },
  { id: 'auditPeriodicity', label: 'Audit Period (Months)', group: 'Dates' },
  { id: 'auditExpiry', label: 'Audit Expiry', group: 'Dates' },
  { id: 'calibrationDate', label: 'Last Calibration', group: 'Dates' },
  { id: 'calibrationPeriodicity', label: 'Calibration Period (Months)', group: 'Dates' },
  { id: 'calibrationExpiry', label: 'Calibration Expiry', group: 'Dates' },
  { id: 'certificationDate', label: 'Last Certification', group: 'Dates' },
  { id: 'certificationPeriodicity', label: 'Certification Period (Months)', group: 'Dates' },
  { id: 'certificationExpiry', label: 'Certification Expiry', group: 'Dates' },
  { id: 'testDate', label: 'Last PAT', group: 'Dates' },
  { id: 'testPeriodicity', label: 'PAT Period (Months)', group: 'Dates' },
  { id: 'patTestExpiry', label: 'PAT Expiry', group: 'Dates' },
  { id: 'nextExpiry', label: 'Next Expiry', group: 'Dates' },
  { id: 'nextExpiryType', label: 'Expiry Type', group: 'Dates' },
  { id: 'notes', label: 'Notes', group: 'Other' },
];

// Risk calculation helper (used for both InfoSec impact and Business severity)
const calculateRiskScore = (likelihood: string, impactOrSeverity: string): number => {
  const likelihoodValues: Record<string, number> = {
    'Rare': 1, 'Unlikely': 2, 'Possible': 3, 'Likely': 4, 'Almost Certain': 5
  };
  const impactValues: Record<string, number> = {
    'Insignificant': 1, 'Minor': 2, 'Moderate': 3, 'Serious': 4, 'Catastrophic': 5
  };
  return (likelihoodValues[likelihood] || 1) * (impactValues[impactOrSeverity] || 1);
};

const getRagColor = (score: number): string => {
  if (score >= 15) return 'bg-red-500';
  if (score >= 10) return 'bg-orange-500';
  if (score >= 5) return 'bg-yellow-500';
  return 'bg-green-500';
};

function ReportsModule() {
  const assetSnap = useSnapshot(assetStore);
  const riskSnap = useSnapshot(riskStore);
  const nonInfoSecSnap = useSnapshot(nonInfoSecRiskStore);
  const { showToast, ToastContainer } = useToast();

  // Get arrays from stores
  const assetsArray = useMemo(() => {
    if (!assetSnap) return [];
    if (assetSnap.assets && Array.isArray(assetSnap.assets)) return assetSnap.assets as Asset[];
    return [];
  }, [assetSnap]);

  const risksArray = useMemo(() => {
    if (!riskSnap) return [];
    if (riskSnap.risks && Array.isArray(riskSnap.risks)) return riskSnap.risks as Risk[];
    return [];
  }, [riskSnap]);

  const businessRisksArray = useMemo(() => {
    if (!nonInfoSecSnap) return [];
    if (nonInfoSecSnap.risks && Array.isArray(nonInfoSecSnap.risks)) return nonInfoSecSnap.risks as NonInfoSecRisk[];
    return [];
  }, [nonInfoSecSnap]);

  // Main tab state
  const [mainTab, setMainTab] = useState<'assets' | 'infosec' | 'business'>('assets');

  // Asset report configuration state - collapsed by default so table is immediately visible
  const [showQuickReports, setShowQuickReports] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [selectedFields, setSelectedFields] = useState([
    'referenceNumber', 'make', 'model', 'assetGroup', 'sku', 'quantity', 'location', 'nextExpiry', 'nextExpiryType'
  ]);
  const [columnOrder, setColumnOrder] = useState([...selectedFields]);
  const [filters, setFilters] = useState({
    assetOwner: 'all',
    status: 'all',
    isISO27001: 'all',
    expiryFrom: '',
    expiryTo: '',
  });
  const [sortBy, setSortBy] = useState('make');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [exportFormat, setExportFormat] = useState<'csv' | 'pdf'>('csv');
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);

  // InfoSec risk analytics state
  const [riskDateRange, setRiskDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('all');
  const [riskCategory, setRiskCategory] = useState<string>('all');
  const [riskTab, setRiskTab] = useState<'overview' | 'matrix' | 'controls'>('overview');

  // Business risk analytics state
  const [bizRiskDateRange, setBizRiskDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('all');
  const [bizRiskCategory, setBizRiskCategory] = useState<string>('all');
  const [bizRiskTab, setBizRiskTab] = useState<'overview' | 'matrix' | 'categories'>('overview');

  // Filtered and sorted asset items
  const filteredItems = useMemo(() => {
    let items = [...assetsArray];

    if (filters.assetOwner !== 'all') {
      items = items.filter((i) => i.assetOwner === filters.assetOwner);
    }
    if (filters.isISO27001 !== 'all') {
      items = items.filter((i) =>
        filters.isISO27001 === 'yes' ? i.isISO27001 : !i.isISO27001
      );
    }
    if (filters.status !== 'all') {
      items = items.filter((i) => {
        const today = new Date();
        const thirtyDays = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
        const checks = [
          calculateExpiry(i.calibrationDate, i.calibrationPeriodicity),
          calculateExpiry(i.certificationDate, i.certificationPeriodicity),
          calculateExpiry(i.testDate, i.testPeriodicity),
          calculateExpiry(i.auditDate, i.auditPeriodicity),
        ].filter((d): d is Date => d !== null);

        if (filters.status === 'overdue') {
          return checks.some((d) => d < today);
        } else if (filters.status === 'upcoming') {
          return checks.some((d) => d >= today && d <= thirtyDays);
        }
        return true;
      });
    }

    // Apply expiry date range filter
    if (filters.expiryFrom || filters.expiryTo) {
      items = items.filter((i) => {
        // Get the next expiry date for this item
        const expiries = [
          calculateExpiry(i.calibrationDate, i.calibrationPeriodicity),
          calculateExpiry(i.certificationDate, i.certificationPeriodicity),
          calculateExpiry(i.testDate, i.testPeriodicity),
          calculateExpiry(i.auditDate, i.auditPeriodicity),
        ].filter((d): d is Date => d !== null);

        if (expiries.length === 0) return false;

        const nextExpiry = expiries.sort((a, b) => a.getTime() - b.getTime())[0];
        
        // If only To date specified, show all items up to that date
        if (!filters.expiryFrom && filters.expiryTo) {
          const toDate = new Date(filters.expiryTo);
          toDate.setHours(23, 59, 59, 999);
          return nextExpiry <= toDate;
        }
        
        // If From date specified
        if (filters.expiryFrom) {
          const fromDate = new Date(filters.expiryFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (nextExpiry < fromDate) return false;
        }
        
        // If To date specified
        if (filters.expiryTo) {
          const toDate = new Date(filters.expiryTo);
          toDate.setHours(23, 59, 59, 999);
          if (nextExpiry > toDate) return false;
        }
        
        return true;
      });
    }

    items.sort((a, b) => {
      const aVal = (a as any)[sortBy] || '';
      const bVal = (b as any)[sortBy] || '';
      const comparison = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return items;
  }, [assetsArray, filters, sortBy, sortDirection]);

  // Filtered risks
  const filteredRisks = useMemo(() => {
    let risks = [...risksArray];

    if (riskDateRange !== 'all') {
      const days = riskDateRange === '7d' ? 7 : riskDateRange === '30d' ? 30 : 90;
      const cutoffDate = subDays(new Date(), days);
      risks = risks.filter((r) => {
        if (!r.lastDateReviewed) return true;
        try {
          return isAfter(parseISO(r.lastDateReviewed), cutoffDate);
        } catch {
          return true;
        }
      });
    }

    if (riskCategory !== 'all') {
      risks = risks.filter((r) => r.assetCategory === riskCategory);
    }

    return risks;
  }, [risksArray, riskDateRange, riskCategory]);

  // Risk statistics
  const riskStats = useMemo(() => {
    const totalRisks = filteredRisks.length;
    const ragDistribution = { critical: 0, high: 0, medium: 0, low: 0 };
    const managedRagDistribution = { critical: 0, high: 0, medium: 0, low: 0 };

    filteredRisks.forEach((risk) => {
      const ucScore = calculateRiskScore(risk.ucLikelihood, risk.ucImpact);
      if (ucScore >= 15) ragDistribution.critical++;
      else if (ucScore >= 10) ragDistribution.high++;
      else if (ucScore >= 5) ragDistribution.medium++;
      else ragDistribution.low++;

      const mScore = calculateRiskScore(risk.managedLikelihood, risk.managedImpact);
      if (mScore >= 15) managedRagDistribution.critical++;
      else if (mScore >= 10) managedRagDistribution.high++;
      else if (mScore >= 5) managedRagDistribution.medium++;
      else managedRagDistribution.low++;
    });

    const usedControls = new Set(filteredRisks.flatMap((r) =>
      r.controls?.length > 0
        ? r.controls.map(c => c.control).filter(Boolean)
        : r.control ? [r.control] : []
    ));
    const controlCoverage = ((usedControls.size / iso27001Controls.length) * 100).toFixed(1);

    return { totalRisks, ragDistribution, managedRagDistribution, usedControls: usedControls.size, controlCoverage };
  }, [filteredRisks]);

  // Filtered business risks
  const filteredBizRisks = useMemo(() => {
    let risks = [...businessRisksArray];

    if (bizRiskDateRange !== 'all') {
      const days = bizRiskDateRange === '7d' ? 7 : bizRiskDateRange === '30d' ? 30 : 90;
      const cutoffDate = subDays(new Date(), days);
      risks = risks.filter((r) => {
        if (!r.lastDateReviewed) return true;
        try {
          return isAfter(parseISO(r.lastDateReviewed), cutoffDate);
        } catch {
          return true;
        }
      });
    }

    if (bizRiskCategory !== 'all') {
      risks = risks.filter((r) => r.riskCategory.includes(bizRiskCategory));
    }

    return risks;
  }, [businessRisksArray, bizRiskDateRange, bizRiskCategory]);

  // Business risk statistics
  const bizRiskStats = useMemo(() => {
    const totalRisks = filteredBizRisks.length;
    const ragDistribution = { critical: 0, high: 0, medium: 0, low: 0 };
    const managedRagDistribution = { critical: 0, high: 0, medium: 0, low: 0 };

    filteredBizRisks.forEach((risk) => {
      const ucScore = calculateRiskScore(risk.ucLikelihood, risk.ucSeverity);
      if (ucScore >= 15) ragDistribution.critical++;
      else if (ucScore >= 10) ragDistribution.high++;
      else if (ucScore >= 5) ragDistribution.medium++;
      else ragDistribution.low++;

      const mScore = calculateRiskScore(risk.managedLikelihood, risk.managedSeverity);
      if (mScore >= 15) managedRagDistribution.critical++;
      else if (mScore >= 10) managedRagDistribution.high++;
      else if (mScore >= 5) managedRagDistribution.medium++;
      else managedRagDistribution.low++;
    });

    // Category breakdown
    const categoryBreakdown: Record<string, number> = {};
    filteredBizRisks.forEach((risk) => {
      risk.riskCategory.forEach((cat) => {
        categoryBreakdown[cat] = (categoryBreakdown[cat] || 0) + 1;
      });
    });

    // Treatment breakdown
    const treatmentBreakdown: Record<string, number> = {};
    filteredBizRisks.forEach((risk) => {
      const t = risk.treatment || 'Unassigned';
      treatmentBreakdown[t] = (treatmentBreakdown[t] || 0) + 1;
    });

    // Status breakdown
    const statusBreakdown: Record<string, number> = {};
    filteredBizRisks.forEach((risk) => {
      const s = risk.controlStatus || 'Not Started';
      statusBreakdown[s] = (statusBreakdown[s] || 0) + 1;
    });

    return { totalRisks, ragDistribution, managedRagDistribution, categoryBreakdown, treatmentBreakdown, statusBreakdown };
  }, [filteredBizRisks]);

  // Business risk category options for filter dropdown
  const bizRiskCategoryOptions = useMemo(() => {
    const cats = new Set<string>();
    businessRisksArray.forEach((r) => r.riskCategory.forEach((c) => cats.add(c)));
    return ['all', ...Array.from(cats).sort()];
  }, [businessRisksArray]);

  // Get unique values for filters
  const assetOwners = useMemo(
    () => [...new Set(assetsArray.map((i) => i.assetOwner).filter(Boolean))],
    [assetsArray]
  );
  const riskCategories = useMemo(
    () => ['all', ...new Set(risksArray.map((r) => r.assetCategory).filter(Boolean))],
    [risksArray]
  );

  // Quick report templates
  const defaultFilters = { assetOwner: 'all', status: 'all', isISO27001: 'all', expiryFrom: '', expiryTo: '' };

  const applyQuickReport = (template: string) => {
    // Always reset filters, sort, and direction first so no state leaks between templates
    setFilters(defaultFilters);
    setSortBy('make');
    setSortDirection('asc');

    switch (template) {
      case 'full':
        setSelectedFields(allFields.map(f => f.id));
        setColumnOrder(allFields.map(f => f.id));
        break;
      case 'compliance':
        setSelectedFields(['referenceNumber', 'make', 'model', 'isISO27001', 'iso27001Category', 'auditDate', 'auditExpiry']);
        setColumnOrder(['referenceNumber', 'make', 'model', 'isISO27001', 'iso27001Category', 'auditDate', 'auditExpiry']);
        setFilters({ ...defaultFilters, isISO27001: 'yes' });
        break;
      case 'overdue':
        setSelectedFields(['referenceNumber', 'make', 'model', 'location', 'nextExpiry', 'nextExpiryType']);
        setColumnOrder(['referenceNumber', 'make', 'model', 'location', 'nextExpiry', 'nextExpiryType']);
        setFilters({ ...defaultFilters, status: 'overdue' });
        break;
      case 'maintenance':
        setSelectedFields(['referenceNumber', 'make', 'model', 'calibrationDate', 'calibrationExpiry', 'testDate', 'patTestExpiry']);
        setColumnOrder(['referenceNumber', 'make', 'model', 'calibrationDate', 'calibrationExpiry', 'testDate', 'patTestExpiry']);
        setFilters({ ...defaultFilters, status: 'upcoming' });
        break;
      case 'byCategory':
        setSelectedFields(['referenceNumber', 'make', 'model', 'assetGroup', 'quantity', 'location', 'owner']);
        setColumnOrder(['referenceNumber', 'make', 'model', 'assetGroup', 'quantity', 'location', 'owner']);
        setSortBy('assetGroup');
        break;
      case 'byOwner':
        setSelectedFields(['referenceNumber', 'make', 'model', 'owner', 'location']);
        setColumnOrder(['referenceNumber', 'make', 'model', 'owner', 'location']);
        setSortBy('owner');
        break;
      case 'summary':
        setSelectedFields(['referenceNumber', 'make', 'model', 'location', 'nextExpiry']);
        setColumnOrder(['referenceNumber', 'make', 'model', 'location', 'nextExpiry']);
        break;
    }
    setShowQuickReports(false);
    showToast('Template applied', 'success');
  };

  const toggleField = (fieldId: string) => {
    if (selectedFields.includes(fieldId)) {
      setSelectedFields(selectedFields.filter((f) => f !== fieldId));
      setColumnOrder(columnOrder.filter((f) => f !== fieldId));
    } else {
      setSelectedFields([...selectedFields, fieldId]);
      setColumnOrder([...columnOrder, fieldId]);
    }
  };

  // Column reordering handlers
  const handleDragStart = (e: React.DragEvent, fieldId: string) => {
    setDraggedColumn(fieldId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetFieldId: string) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetFieldId) return;

    const newOrder = [...columnOrder];
    const draggedIdx = newOrder.indexOf(draggedColumn);
    const targetIdx = newOrder.indexOf(targetFieldId);

    newOrder.splice(draggedIdx, 1);
    newOrder.splice(targetIdx, 0, draggedColumn);

    setColumnOrder(newOrder);
    setDraggedColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  const moveColumn = (fieldId: string, direction: 'left' | 'right') => {
    const idx = columnOrder.indexOf(fieldId);
    if (direction === 'left' && idx > 0) {
      const newOrder = [...columnOrder];
      [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
      setColumnOrder(newOrder);
    } else if (direction === 'right' && idx < columnOrder.length - 1) {
      const newOrder = [...columnOrder];
      [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
      setColumnOrder(newOrder);
    }
  };

  const handleExport = () => {
    if (mainTab === 'assets') {
      if (filteredItems.length === 0) {
        showToast('No data to export', 'error');
        return;
      }
      exportAssetReport();
    } else if (mainTab === 'infosec') {
      if (filteredRisks.length === 0) {
        showToast('No InfoSec risk data to export', 'error');
        return;
      }
      exportRiskReport();
    } else {
      if (filteredBizRisks.length === 0) {
        showToast('No business risk data to export', 'error');
        return;
      }
      exportBusinessRiskReport();
    }
  };

  const exportAssetReport = () => {
    const rows = filteredItems.map((item) => {
      const row: Record<string, unknown> = {};
      columnOrder.forEach((fieldId) => {
        const field = allFields.find((f) => f.id === fieldId);
        const key = field?.label || fieldId;

        if (fieldId === 'isISO27001') {
          row[key] = item.isISO27001 ? 'Yes' : 'No';
        } else if (fieldId === 'nextExpiry' || fieldId === 'nextExpiryType') {
          const expiries = [
            { type: 'Audit', date: calculateExpiry(item.auditDate, item.auditPeriodicity) },
            { type: 'Calibration', date: calculateExpiry(item.calibrationDate, item.calibrationPeriodicity) },
            { type: 'Certification', date: calculateExpiry(item.certificationDate, item.certificationPeriodicity) },
            { type: 'PAT', date: calculateExpiry(item.testDate, item.testPeriodicity) },
          ].filter((e) => e.date !== null) as { type: string; date: Date }[];
          const next = expiries.sort((a, b) => a.date.getTime() - b.date.getTime())[0];
          row[key] = fieldId === 'nextExpiry' ? (next ? formatDate(next.date) : '-') : (next?.type || '-');
        } else if (fieldId.includes('Expiry')) {
          const mapping: Record<string, { date: string; period: string }> = {
            auditExpiry: { date: 'auditDate', period: 'auditPeriodicity' },
            calibrationExpiry: { date: 'calibrationDate', period: 'calibrationPeriodicity' },
            certificationExpiry: { date: 'certificationDate', period: 'certificationPeriodicity' },
            patTestExpiry: { date: 'testDate', period: 'testPeriodicity' },
          };
          const config = mapping[fieldId];
          if (config) {
            const expiry = calculateExpiry((item as any)[config.date], (item as any)[config.period]);
            row[key] = expiry ? formatDate(expiry) : '-';
          } else {
            row[key] = '-';
          }
        } else if (fieldId.includes('Date')) {
          row[key] = (item as any)[fieldId] ? formatDate((item as any)[fieldId]) : '-';
        } else {
          row[key] = (item as any)[fieldId] || '-';
        }
      });
      return row;
    });

    exportToCSV(rows, 'asset-report');
    showToast('Asset report exported', 'success');
  };

  const exportRiskReport = () => {
    const data = filteredRisks.map((risk) => ({
      'Reference': risk.referenceNumber,
      'Asset': risk.asset || risk.assetCategory,
      'Threat': risk.threat,
      'Vulnerability': risk.vulnerability,
      'Risk Category': risk.riskCategory.join(', '),
      'UC Likelihood': risk.ucLikelihood,
      'UC Impact': risk.ucImpact,
      'UC Score': calculateRiskScore(risk.ucLikelihood, risk.ucImpact),
      'Treatment': risk.treatment,
      'Controls': (risk.controls?.length > 0 ? risk.controls : risk.control ? [{ control: risk.control }] : []).map(c => c.control).join('; '),
      'M Likelihood': risk.managedLikelihood,
      'M Impact': risk.managedImpact,
      'M Score': calculateRiskScore(risk.managedLikelihood, risk.managedImpact),
      'Status': risk.controlStatus,
      'Last Reviewed': risk.lastDateReviewed ? formatDate(risk.lastDateReviewed) : '',
    }));

    exportToCSV(data, 'infosec-risk-report');
    showToast('InfoSec risk report exported', 'success');
  };

  const exportBusinessRiskReport = () => {
    const data = filteredBizRisks.map((risk) => ({
      'Reference': risk.referenceNumber,
      'Title': risk.riskTitle,
      'Categories': risk.riskCategory.join(', '),
      'Risk Owner': risk.riskOwner,
      'Cause': risk.cause,
      'Consequence': risk.consequence,
      'UC Likelihood': risk.ucLikelihood,
      'UC Severity': risk.ucSeverity,
      'UC Score': calculateRiskScore(risk.ucLikelihood, risk.ucSeverity),
      'Treatment': risk.treatment,
      'Mitigation': risk.mitigation,
      'M Likelihood': risk.managedLikelihood,
      'M Severity': risk.managedSeverity,
      'M Score': calculateRiskScore(risk.managedLikelihood, risk.managedSeverity),
      'Status': risk.controlStatus,
      'Last Reviewed': risk.lastDateReviewed ? formatDate(risk.lastDateReviewed) : '',
    }));

    exportToCSV(data, 'business-risk-report');
    showToast('Business risk report exported', 'success');
  };

  return (
    <div className="flex flex-1 overflow-hidden bg-gray-50">
      <NavigationSidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
      <ModuleHeader
        title="Reports & Analytics"
        subtitle="Generate customisable reports and analyse compliance data"
      />

      {/* Toolbar — fixed at top, never scrolls */}
      <div className="flex-shrink-0 px-6 pt-6 pb-0">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          {/* Tab Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setMainTab('assets')}
              className={'px-4 py-2 rounded-lg font-medium text-sm transition ' + (mainTab === 'assets' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100')}
            >
              <Icon name="Package" size={16} className="inline mr-1.5" />
              Asset Reports
            </button>
            <button
              onClick={() => setMainTab('infosec')}
              className={'px-4 py-2 rounded-lg font-medium text-sm transition ' + (mainTab === 'infosec' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100')}
            >
              <Icon name="Lock" size={16} className="inline mr-1.5" />
              InfoSec Risk Analytics
            </button>
            <button
              onClick={() => setMainTab('business')}
              className={'px-4 py-2 rounded-lg font-medium text-sm transition ' + (mainTab === 'business' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100')}
            >
              <Icon name="AlertOctagon" size={16} className="inline mr-1.5" />
              Business Risk Analytics
            </button>
          </div>

          {/* Action buttons */}
          {mainTab === 'assets' && (
            <div className="flex items-center gap-2">
              <Button
                variant={showQuickReports ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => { setShowQuickReports(!showQuickReports); if (!showQuickReports) setShowConfig(false); }}
              >
                <Icon name="FileText" size={16} className="mr-1.5" />
                {showQuickReports ? 'Hide Templates' : 'Show Templates'}
              </Button>
              <Button
                variant={showConfig ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => { setShowConfig(!showConfig); if (!showConfig) setShowQuickReports(false); }}
              >
                <Icon name="Settings" size={16} className="mr-1.5" />
                {showConfig ? 'Hide Config' : 'Show Config'}
              </Button>
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as 'csv' | 'pdf')}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="csv">CSV</option>
                <option value="pdf">PDF</option>
              </select>
              <Button onClick={handleExport} size="sm">
                <Icon name="Download" size={16} className="mr-1.5" />
                Export
              </Button>
            </div>
          )}
          {mainTab === 'infosec' && (
            <Button onClick={handleExport} size="sm">
              <Icon name="Download" size={16} className="mr-1.5" />
              Export InfoSec Report
            </Button>
          )}
          {mainTab === 'business' && (
            <Button onClick={handleExport} size="sm">
              <Icon name="Download" size={16} className="mr-1.5" />
              Export Business Report
            </Button>
          )}
        </div>
      </div>

      {/* Content area — fills remaining space */}
      <div className="flex-1 min-h-0 flex flex-col px-6 pb-6">
        {/* Asset Reports Tab */}
        {mainTab === 'assets' && (
          <div className="flex-1 min-h-0 flex flex-col">
            {/* Collapsible Quick Report Templates */}
            {showQuickReports && (
              <div className="flex-shrink-0 bg-white rounded-lg shadow-md p-4 mb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <QuickReportCard
                    title="Full Inventory"
                    description="All details including ISO 27001 categories"
                    icon="FileText"
                    color="from-blue-500 to-blue-600"
                    stats={[assetsArray.length + ' assets']}
                    onClick={() => applyQuickReport('full')}
                  />
                  <QuickReportCard
                    title="Compliance"
                    description="ISO 27001 assets with audit tracking"
                    icon="Shield"
                    color="from-green-500 to-green-600"
                    stats={[assetsArray.filter(a => a.isISO27001).length + ' ISO assets']}
                    onClick={() => applyQuickReport('compliance')}
                  />
                  <QuickReportCard
                    title="Overdue Items"
                    description="Assets with expired dates"
                    icon="AlertTriangle"
                    color="from-red-500 to-red-600"
                    stats={['Needs attention']}
                    onClick={() => applyQuickReport('overdue')}
                  />
                  <QuickReportCard
                    title="Maintenance"
                    description="Upcoming schedule (next 30 days)"
                    icon="Calendar"
                    color="from-amber-500 to-amber-600"
                    stats={['Next 30 days']}
                    onClick={() => applyQuickReport('maintenance')}
                  />
                  <QuickReportCard
                    title="By Asset Group"
                    description="Grouped by asset group"
                    icon="Filter"
                    color="from-pink-500 to-pink-600"
                    stats={['Grouped view']}
                    onClick={() => applyQuickReport('byCategory')}
                  />
                  <QuickReportCard
                    title="By Owner"
                    description="Grouped by owner/department"
                    icon="Users"
                    color="from-orange-500 to-orange-600"
                    stats={['Owner tracking']}
                    onClick={() => applyQuickReport('byOwner')}
                  />
                  <QuickReportCard
                    title="Summary"
                    description="Key information only"
                    icon="List"
                    color="from-teal-500 to-teal-600"
                    stats={['Essential fields']}
                    onClick={() => applyQuickReport('summary')}
                  />
                  <QuickReportCard
                    title="Custom"
                    description="Your selected fields and filters"
                    icon="Settings"
                    color="from-gray-500 to-gray-600"
                    stats={[selectedFields.length + ' fields']}
                    onClick={() => setShowQuickReports(false)}
                  />
                </div>
              </div>
            )}

            {/* Collapsible Configuration Panel */}
            {showConfig && (
              <div className="flex-shrink-0 bg-white rounded-lg shadow-md p-4 mb-4">
                <div className="space-y-4">
                  {/* Field Selection */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-700">Select Fields to Include</h3>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setSelectedFields(allFields.map(f => f.id)); setColumnOrder(allFields.map(f => f.id)); }}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Select All
                        </button>
                        <button
                          onClick={() => { setSelectedFields([]); setColumnOrder([]); }}
                          className="text-xs text-gray-600 hover:text-gray-800"
                        >
                          Select None
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                      {allFields.map((field) => (
                        <label key={field.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedFields.includes(field.id)}
                            onChange={() => toggleField(field.id)}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="truncate">{field.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Column Order */}
                  {columnOrder.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-2">
                        Column Order <span className="text-xs text-gray-400">(drag to reorder)</span>
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {columnOrder.map((fieldId, index) => {
                          const field = allFields.find((f) => f.id === fieldId);
                          return (
                            <div
                              key={fieldId}
                              draggable
                              onDragStart={(e) => handleDragStart(e, fieldId)}
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDrop(e, fieldId)}
                              onDragEnd={handleDragEnd}
                              className={
                                'flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm cursor-move transition ' +
                                (draggedColumn === fieldId
                                  ? 'bg-purple-100 border-2 border-purple-400'
                                  : 'bg-gray-100 border border-gray-300 hover:bg-gray-200')
                              }
                            >
                              <Icon name="GripVertical" size={14} className="text-gray-400" />
                              <span>{field?.label || fieldId}</span>
                              <div className="flex items-center ml-1">
                                <button
                                  onClick={() => moveColumn(fieldId, 'left')}
                                  disabled={index === 0}
                                  className="p-0.5 hover:bg-gray-300 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                  title="Move left"
                                >
                                  <Icon name="ChevronLeft" size={12} />
                                </button>
                                <button
                                  onClick={() => moveColumn(fieldId, 'right')}
                                  disabled={index === columnOrder.length - 1}
                                  className="p-0.5 hover:bg-gray-300 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                  title="Move right"
                                >
                                  <Icon name="ChevronRight" size={12} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Filters */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Filters</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Asset Owner</label>
                        <select
                          value={filters.assetOwner}
                          onChange={(e) => setFilters({ ...filters, assetOwner: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="all">All Owners</option>
                          {assetOwners.map((owner) => (
                            <option key={owner} value={owner}>{owner}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Status</label>
                        <select
                          value={filters.status}
                          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="all">All Statuses</option>
                          <option value="overdue">Overdue</option>
                          <option value="upcoming">Upcoming (30 days)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Information Security</label>
                        <select
                          value={filters.isISO27001}
                          onChange={(e) => setFilters({ ...filters, isISO27001: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="all">All Assets</option>
                          <option value="yes">Info Sec Only</option>
                          <option value="no">Non-Info Sec</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Expiry From</label>
                        <input
                          type="date"
                          value={filters.expiryFrom}
                          min={new Date().toISOString().split('T')[0]}
                          onChange={(e) => {
                            const newFrom = e.target.value;
                            // If To date is earlier than new From date, clear To date
                            const newTo = filters.expiryTo && filters.expiryTo < newFrom ? '' : filters.expiryTo;
                            setFilters({ ...filters, expiryFrom: newFrom, expiryTo: newTo });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Expiry To</label>
                        <input
                          type="date"
                          value={filters.expiryTo}
                          min={filters.expiryFrom || undefined}
                          onChange={(e) => setFilters({ ...filters, expiryTo: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Sort */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Sort</h3>
                    <div className="flex items-center gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Sort By</label>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          {selectedFields.map((fieldId) => {
                            const field = allFields.find((f) => f.id === fieldId);
                            return (
                              <option key={fieldId} value={fieldId}>{field?.label || fieldId}</option>
                            );
                          })}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Direction</label>
                        <select
                          value={sortDirection}
                          onChange={(e) => setSortDirection(e.target.value as 'asc' | 'desc')}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="asc">Ascending</option>
                          <option value="desc">Descending</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Results Table — fills remaining space */}
            <div className="flex-1 min-h-0 bg-white rounded-lg shadow-md flex flex-col overflow-hidden">
              <div className="flex-shrink-0 p-3 border-b bg-gray-50">
                <p className="text-sm text-gray-600">
                  Showing {filteredItems.length} of {assetsArray.length} assets
                </p>
              </div>
              <div className="flex-1 min-h-0 overflow-auto">
                <table className="w-full min-w-[1200px]">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      {columnOrder.map((fieldId) => {
                        const field = allFields.find((f) => f.id === fieldId);
                        return (
                          <th
                            key={fieldId}
                            draggable
                            onDragStart={(e) => handleDragStart(e, fieldId)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, fieldId)}
                            onDragEnd={handleDragEnd}
                            className={
                              'px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-move select-none ' +
                              (draggedColumn === fieldId ? 'bg-purple-100' : 'hover:bg-gray-100')
                            }
                          >
                            <div className="flex items-center gap-1">
                              <Icon name="GripVertical" size={12} className="text-gray-400" />
                              {field?.label || fieldId}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredItems.length === 0 ? (
                      <tr>
                        <td colSpan={columnOrder.length} className="px-4 py-8 text-center text-gray-500">
                          No assets match the current filters
                        </td>
                      </tr>
                    ) : (
                      filteredItems.map((item) => (
                        <ReportRow key={item.id} item={item} columnOrder={columnOrder} />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* InfoSec Risk Analytics Tab */}
        {mainTab === 'infosec' && (
          <div className="flex-1 min-h-0 overflow-auto">
            {/* Risk Filters & Sub-tabs */}
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="flex items-center gap-3">
                <select
                  value={riskDateRange}
                  onChange={(e) => setRiskDateRange(e.target.value as typeof riskDateRange)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="all">All Time</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                </select>
                <select
                  value={riskCategory}
                  onChange={(e) => setRiskCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  {riskCategories.map((cat) => (
                    <option key={cat} value={cat}>{cat === 'all' ? 'All Categories' : cat}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
              {[
                { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
                { id: 'matrix', label: 'Risk Matrix', icon: 'Grid' },
                { id: 'controls', label: 'Control Coverage', icon: 'Shield' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setRiskTab(tab.id as typeof riskTab)}
                  className={'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ' + (riskTab === tab.id ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100')}
                >
                  <Icon name={tab.icon as IconName} size={18} />
                  {tab.label}
                </button>
              ))}
              </div>
            </div>

            {/* Overview */}
            {riskTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard title="Total Risks" value={riskStats.totalRisks} icon="AlertTriangle" color="orange" />
                  <StatCard title="Critical" value={riskStats.ragDistribution.critical} icon="AlertOctagon" color="red" />
                  <StatCard title="Used Controls" value={riskStats.usedControls} icon="Shield" color="green" />
                  <StatCard title="Coverage" value={riskStats.controlCoverage + '%'} icon="PieChart" color="purple" />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Uncontrolled Risk Distribution</h3>
                    <div className="space-y-3">
                      <RagBar label="Critical" count={riskStats.ragDistribution.critical} total={riskStats.totalRisks} color="red" />
                      <RagBar label="High" count={riskStats.ragDistribution.high} total={riskStats.totalRisks} color="orange" />
                      <RagBar label="Medium" count={riskStats.ragDistribution.medium} total={riskStats.totalRisks} color="yellow" />
                      <RagBar label="Low" count={riskStats.ragDistribution.low} total={riskStats.totalRisks} color="green" />
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Managed Risk Distribution</h3>
                    <div className="space-y-3">
                      <RagBar label="Critical" count={riskStats.managedRagDistribution.critical} total={riskStats.totalRisks} color="red" />
                      <RagBar label="High" count={riskStats.managedRagDistribution.high} total={riskStats.totalRisks} color="orange" />
                      <RagBar label="Medium" count={riskStats.managedRagDistribution.medium} total={riskStats.totalRisks} color="yellow" />
                      <RagBar label="Low" count={riskStats.managedRagDistribution.low} total={riskStats.totalRisks} color="green" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Risk Matrix */}
            {riskTab === 'matrix' && (
              <div className="grid md:grid-cols-2 gap-6">
                <RiskMatrixChart title="Uncontrolled Risk Matrix" risks={filteredRisks} type="uncontrolled" />
                <RiskMatrixChart title="Managed Risk Matrix" risks={filteredRisks} type="managed" />
              </div>
            )}

            {/* Control Coverage */}
            {riskTab === 'controls' && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Controls by Theme</h3>
                  <div className="space-y-4">
                    {controlThemes.map((theme) => {
                      const themeControls = iso27001Controls.filter((c) => c.theme === theme);
                      const usedInTheme = themeControls.filter((c) =>
                        filteredRisks.some((r) =>
                          r.controls?.some(ctrl => ctrl.control === c.control) || r.control === c.control
                        )
                      ).length;
                      const percent = ((usedInTheme / themeControls.length) * 100).toFixed(0);

                      return (
                        <div key={theme}>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700">{theme}</span>
                            <span className="text-sm text-gray-500">{usedInTheme}/{themeControls.length} ({percent}%)</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div className="bg-purple-500 h-3 rounded-full transition-all" style={{ width: percent + '%' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Business Risk Analytics Tab */}
        {mainTab === 'business' && (
          <div className="flex-1 min-h-0 overflow-auto">
            {/* Business Risk Filters & Sub-tabs */}
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="flex items-center gap-3">
                <select
                  value={bizRiskDateRange}
                  onChange={(e) => setBizRiskDateRange(e.target.value as typeof bizRiskDateRange)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="all">All Time</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                </select>
                <select
                  value={bizRiskCategory}
                  onChange={(e) => setBizRiskCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  {bizRiskCategoryOptions.map((cat) => (
                    <option key={cat} value={cat}>{cat === 'all' ? 'All Categories' : cat}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
              {[
                { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
                { id: 'matrix', label: 'Risk Matrix', icon: 'Grid' },
                { id: 'categories', label: 'Category Breakdown', icon: 'PieChart' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setBizRiskTab(tab.id as typeof bizRiskTab)}
                  className={'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ' + (bizRiskTab === tab.id ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100')}
                >
                  <Icon name={tab.icon as IconName} size={18} />
                  {tab.label}
                </button>
              ))}
              </div>
            </div>

            {/* Business Risk Overview */}
            {bizRiskTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard title="Total Risks" value={bizRiskStats.totalRisks} icon="AlertOctagon" color="orange" />
                  <StatCard title="Critical" value={bizRiskStats.ragDistribution.critical} icon="AlertOctagon" color="red" />
                  <StatCard title="Categories" value={Object.keys(bizRiskStats.categoryBreakdown).length} icon="Tag" color="blue" />
                  <StatCard title="Treated" value={bizRiskStats.statusBreakdown['Implemented'] || 0} icon="CheckCircle" color="green" />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Uncontrolled Risk Distribution</h3>
                    <div className="space-y-3">
                      <RagBar label="Critical" count={bizRiskStats.ragDistribution.critical} total={bizRiskStats.totalRisks} color="red" />
                      <RagBar label="High" count={bizRiskStats.ragDistribution.high} total={bizRiskStats.totalRisks} color="orange" />
                      <RagBar label="Medium" count={bizRiskStats.ragDistribution.medium} total={bizRiskStats.totalRisks} color="yellow" />
                      <RagBar label="Low" count={bizRiskStats.ragDistribution.low} total={bizRiskStats.totalRisks} color="green" />
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Managed Risk Distribution</h3>
                    <div className="space-y-3">
                      <RagBar label="Critical" count={bizRiskStats.managedRagDistribution.critical} total={bizRiskStats.totalRisks} color="red" />
                      <RagBar label="High" count={bizRiskStats.managedRagDistribution.high} total={bizRiskStats.totalRisks} color="orange" />
                      <RagBar label="Medium" count={bizRiskStats.managedRagDistribution.medium} total={bizRiskStats.totalRisks} color="yellow" />
                      <RagBar label="Low" count={bizRiskStats.managedRagDistribution.low} total={bizRiskStats.totalRisks} color="green" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Business Risk Matrix */}
            {bizRiskTab === 'matrix' && (
              <div className="grid md:grid-cols-2 gap-6">
                <BusinessRiskMatrixChart title="Uncontrolled Risk Matrix" risks={filteredBizRisks} type="uncontrolled" />
                <BusinessRiskMatrixChart title="Managed Risk Matrix" risks={filteredBizRisks} type="managed" />
              </div>
            )}

            {/* Category Breakdown */}
            {bizRiskTab === 'categories' && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Risks by Category</h3>
                    <div className="space-y-4">
                      {Object.entries(bizRiskStats.categoryBreakdown)
                        .sort(([, a], [, b]) => b - a)
                        .map(([category, count]) => {
                          const percent = bizRiskStats.totalRisks > 0
                            ? ((count / bizRiskStats.totalRisks) * 100).toFixed(0)
                            : '0';
                          return (
                            <div key={category}>
                              <div className="flex justify-between mb-1">
                                <span className="text-sm font-medium text-gray-700">{category}</span>
                                <span className="text-sm text-gray-500">{count} ({percent}%)</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3">
                                <div className="bg-purple-500 h-3 rounded-full transition-all" style={{ width: percent + '%' }} />
                              </div>
                            </div>
                          );
                        })}
                      {Object.keys(bizRiskStats.categoryBreakdown).length === 0 && (
                        <p className="text-sm text-gray-500">No risk categories to display</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Risks by Treatment</h3>
                    <div className="space-y-4">
                      {Object.entries(bizRiskStats.treatmentBreakdown)
                        .sort(([, a], [, b]) => b - a)
                        .map(([treatment, count]) => {
                          const percent = bizRiskStats.totalRisks > 0
                            ? ((count / bizRiskStats.totalRisks) * 100).toFixed(0)
                            : '0';
                          const treatmentColors: Record<string, string> = {
                            'Treat the risk': 'bg-blue-500',
                            'Avoid the risk': 'bg-red-500',
                            'Transfer the risk': 'bg-amber-500',
                            'Accept the risk': 'bg-green-500',
                          };
                          return (
                            <div key={treatment}>
                              <div className="flex justify-between mb-1">
                                <span className="text-sm font-medium text-gray-700">{treatment}</span>
                                <span className="text-sm text-gray-500">{count} ({percent}%)</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3">
                                <div className={(treatmentColors[treatment] || 'bg-gray-500') + ' h-3 rounded-full transition-all'} style={{ width: percent + '%' }} />
                              </div>
                            </div>
                          );
                        })}
                      {Object.keys(bizRiskStats.treatmentBreakdown).length === 0 && (
                        <p className="text-sm text-gray-500">No treatments to display</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-md p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Risks by Status</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {Object.entries(bizRiskStats.statusBreakdown)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([status, count]) => {
                        const statusColors: Record<string, string> = {
                          'Not Started': 'bg-gray-100 text-gray-700 border-gray-300',
                          'In Progress': 'bg-blue-50 text-blue-700 border-blue-300',
                          'Under Review': 'bg-amber-50 text-amber-700 border-amber-300',
                          'Implemented': 'bg-green-50 text-green-700 border-green-300',
                          'Not Applicable': 'bg-gray-50 text-gray-500 border-gray-200',
                        };
                        return (
                          <div key={status} className={'rounded-lg border p-4 text-center ' + (statusColors[status] || 'bg-gray-50 text-gray-700 border-gray-200')}>
                            <p className="text-2xl font-bold">{count}</p>
                            <p className="text-xs font-medium mt-1">{status}</p>
                          </div>
                        );
                      })}
                    {Object.keys(bizRiskStats.statusBreakdown).length === 0 && (
                      <p className="text-sm text-gray-500 col-span-full text-center">No status data to display</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <ToastContainer />
      </div>
    </div>
  );
}

// Quick Report Card Component
function QuickReportCard({
  title,
  description,
  icon,
  color,
  stats,
  onClick,
}: {
  title: string;
  description: string;
  icon: string;
  color: string;
  stats: string[];
  onClick: () => void;
}) {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="text-left p-3 rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-purple-300 transition-all"
    >
      <div className="flex items-center gap-2 mb-1">
        <div className={'p-1.5 rounded bg-gradient-to-r ' + color + ' text-white'}>
          <Icon name={icon as IconName} size={14} />
        </div>
        <h3 className="font-semibold text-sm text-gray-800">{title}</h3>
      </div>
      <p className="text-xs text-gray-500">{description}</p>
      <div className="text-xs text-gray-400 mt-1">
        {stats.map((stat, i) => (
          <span key={i}>• {stat} </span>
        ))}
      </div>
    </motion.button>
  );
}

// Report Row Component
function ReportRow({ item, columnOrder }: { item: Asset; columnOrder: string[] }) {
  const getCellValue = (fieldId: string) => {
    if (fieldId === 'isISO27001') {
      return item.isISO27001 ? (
        <span className="text-green-600 font-medium">Yes</span>
      ) : (
        <span className="text-gray-400">No</span>
      );
    }

    if (fieldId === 'nextExpiry' || fieldId === 'nextExpiryType') {
      const expiries = [
        { type: 'Audit', date: calculateExpiry(item.auditDate, item.auditPeriodicity) },
        { type: 'Calibration', date: calculateExpiry(item.calibrationDate, item.calibrationPeriodicity) },
        { type: 'Certification', date: calculateExpiry(item.certificationDate, item.certificationPeriodicity) },
        { type: 'PAT', date: calculateExpiry(item.testDate, item.testPeriodicity) },
      ].filter((e) => e.date !== null) as { type: string; date: Date }[];

      const next = expiries.sort((a, b) => a.date.getTime() - b.date.getTime())[0];

      if (fieldId === 'nextExpiry') {
        if (next) {
          const status = getRAGStatus(next.date);
          const colors = { red: 'text-red-600', amber: 'text-amber-600', green: 'text-green-600' };
          return <span className={colors[status]}>{formatDate(next.date)}</span>;
        }
        return '-';
      } else {
        return next?.type || '-';
      }
    }

    if (fieldId.includes('Expiry')) {
      const mapping: Record<string, { date: string; period: string }> = {
        auditExpiry: { date: 'auditDate', period: 'auditPeriodicity' },
        calibrationExpiry: { date: 'calibrationDate', period: 'calibrationPeriodicity' },
        certificationExpiry: { date: 'certificationDate', period: 'certificationPeriodicity' },
        patTestExpiry: { date: 'testDate', period: 'testPeriodicity' },
      };
      const config = mapping[fieldId];
      if (config) {
        const expiry = calculateExpiry((item as any)[config.date], (item as any)[config.period]);
        if (expiry) {
          const status = getRAGStatus(expiry);
          const colors = { red: 'text-red-600', amber: 'text-amber-600', green: 'text-green-600' };
          return <span className={colors[status]}>{formatDate(expiry)}</span>;
        }
      }
      return '-';
    }

    if (fieldId.includes('Date')) {
      return (item as any)[fieldId] ? formatDate((item as any)[fieldId]) : '-';
    }

    return (item as any)[fieldId] || '-';
  };

  return (
    <tr className="hover:bg-gray-50">
      {columnOrder.map((fieldId) => (
        <td key={fieldId} className="px-4 py-3 text-sm text-gray-900">
          {getCellValue(fieldId)}
        </td>
      ))}
    </tr>
  );
}

// Stat Card Component
function StatCard({
  title,
  value,
  icon,
  color,
}: {
  title: string;
  value: string | number;
  icon: string;
  color: 'blue' | 'purple' | 'orange' | 'green' | 'red' | 'gray';
}) {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
    gray: 'bg-gray-50 text-gray-600',
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-gray-800 mt-1">{value}</p>
        </div>
        <div className={'p-3 rounded-lg ' + colorClasses[color]}>
          <Icon name={icon as IconName} size={24} />
        </div>
      </div>
    </motion.div>
  );
}

// RAG Bar Component
function RagBar({
  label,
  count,
  total,
  color,
}: {
  label: string;
  count: number;
  total: number;
  color: 'red' | 'orange' | 'yellow' | 'green';
}) {
  const percent = total > 0 ? ((count / total) * 100).toFixed(0) : '0';
  const colorClasses = {
    red: 'bg-red-500',
    orange: 'bg-orange-500',
    yellow: 'bg-yellow-500',
    green: 'bg-green-500',
  };

  return (
    <div>
      <div className="flex justify-between mb-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-sm text-gray-500">{count} ({percent}%)</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div className={colorClasses[color] + ' h-2 rounded-full transition-all'} style={{ width: percent + '%' }} />
      </div>
    </div>
  );
}

// Risk Matrix Chart Component
function RiskMatrixChart({
  title,
  risks,
  type,
}: {
  title: string;
  risks: Risk[];
  type: 'uncontrolled' | 'managed';
}) {
  const matrixSnap = useSnapshot(riskMatrixStore);

  // Read labels from the store so they match the admin config
  const likelihoodLabels = matrixSnap.infoSec.likelihoodLabels.map(l => l.label);
  const impactLabels = matrixSnap.infoSec.impactLabels.map(l => l.label);

  // Matrix rows use same order as store/admin: index 0 = Rare (top), index 4 = Almost Certain (bottom)
  const matrix: number[][] = Array(5).fill(null).map(() => Array(5).fill(0));

  const likelihoodMap: Record<string, number> = {};
  likelihoodLabels.forEach((label, idx) => { likelihoodMap[label] = idx; });
  const impactMap: Record<string, number> = {};
  impactLabels.forEach((label, idx) => { impactMap[label] = idx; });

  risks.forEach((risk) => {
    const likelihood = type === 'uncontrolled' ? risk.ucLikelihood : risk.managedLikelihood;
    const impact = type === 'uncontrolled' ? risk.ucImpact : risk.managedImpact;
    const lIdx = likelihoodMap[likelihood] ?? 0;
    const iIdx = impactMap[impact] ?? 0;
    matrix[lIdx][iIdx]++;
  });

  // Get cell colour directly from the store (row index = store index, no conversion needed)
  const getCellColor = (row: number, col: number): string => {
    const ragColor = matrixSnap.infoSec.colors[row]?.[col] ?? 'green';
    return riskMatrixActions.getColorClass(ragColor);
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <h3 className="text-lg font-bold text-gray-800 mb-4">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className="p-2 text-xs text-gray-500"></th>
              {impactLabels.map((label) => (
                <th key={label} className="p-2 text-xs text-gray-500 text-center">{label.substring(0, 3)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.map((row, rowIdx) => (
              <tr key={rowIdx}>
                <td className="p-2 text-xs text-gray-500 text-right pr-3">{likelihoodLabels[rowIdx].substring(0, 3)}</td>
                {row.map((count, colIdx) => (
                  <td key={colIdx} className="p-1">
                    <div className={getCellColor(rowIdx, colIdx) + ' w-10 h-10 flex items-center justify-center rounded text-white font-bold text-sm mx-auto'}>
                      {count > 0 ? count : ''}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Business Risk Matrix Chart Component (uses nonInfoSec colours and severity labels)
function BusinessRiskMatrixChart({
  title,
  risks,
  type,
}: {
  title: string;
  risks: NonInfoSecRisk[];
  type: 'uncontrolled' | 'managed';
}) {
  const matrixSnap = useSnapshot(riskMatrixStore);

  // Read labels from the nonInfoSec config
  const likelihoodLabels = matrixSnap.nonInfoSec.likelihoodLabels.map(l => l.label);
  const severityLabels = matrixSnap.nonInfoSec.severityLabels.map(l => l.label);

  const matrix: number[][] = Array(5).fill(null).map(() => Array(5).fill(0));

  const likelihoodMap: Record<string, number> = {};
  likelihoodLabels.forEach((label, idx) => { likelihoodMap[label] = idx; });
  const severityMap: Record<string, number> = {};
  severityLabels.forEach((label, idx) => { severityMap[label] = idx; });

  risks.forEach((risk) => {
    const likelihood = type === 'uncontrolled' ? risk.ucLikelihood : risk.managedLikelihood;
    const severity = type === 'uncontrolled' ? risk.ucSeverity : risk.managedSeverity;
    const lIdx = likelihoodMap[likelihood] ?? 0;
    const sIdx = severityMap[severity] ?? 0;
    matrix[lIdx][sIdx]++;
  });

  // Get cell colour from the nonInfoSec config
  const getCellColor = (row: number, col: number): string => {
    const ragColor = matrixSnap.nonInfoSec.colors[row]?.[col] ?? 'green';
    return riskMatrixActions.getColorClass(ragColor);
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <h3 className="text-lg font-bold text-gray-800 mb-4">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className="p-2 text-xs text-gray-500"></th>
              {severityLabels.map((label) => (
                <th key={label} className="p-2 text-xs text-gray-500 text-center">{label.substring(0, 3)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.map((row, rowIdx) => (
              <tr key={rowIdx}>
                <td className="p-2 text-xs text-gray-500 text-right pr-3">{likelihoodLabels[rowIdx].substring(0, 3)}</td>
                {row.map((count, colIdx) => (
                  <td key={colIdx} className="p-1">
                    <div className={getCellColor(rowIdx, colIdx) + ' w-10 h-10 flex items-center justify-center rounded text-white font-bold text-sm mx-auto'}>
                      {count > 0 ? count : ''}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
